// styled components
import {
  Link,
  SingleLink,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledDrawer
} from './styles';

// components
import Logo from '@/components/Logo';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { NavLink, useLocation } from 'react-router-dom';

// hooks
import { useSidebar } from '@/contexts/SidebarContext';
import { useWindowSize } from 'react-use';
import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { USER_ROLES } from '@/constants';

const Sidebar = () => {
  const { open, setOpen } = useSidebar();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation('common');
  const isAdmin = user?.role === USER_ROLES.ADMIN;

  const [expanded, setExpanded] = useState<string | undefined>(undefined);
  const { pathname } = useLocation();
  const { width } = useWindowSize();
  const isLandingPage = !isAuthenticated && pathname === '/';

  const HOME_LINK = useMemo(() => ({
    title: t('navHome'),
    icon: 'house',
    path: '/dashboard',
  }), [t]);

  const AUTH_LINKS = useMemo(() => [
    {
      title: t('navMatchesBrowser'),
      icon: 'ball',
      pages: [
        { title: t('navAllLeagues'), path: '/matches' },
      ],
    },
    {
      title: t('navTournaments'),
      icon: 'ball',
      pages: [
        { title: t('navMyTournaments'), path: '/tournaments' },
        { title: t('navPublicTournaments'), path: '/tournaments/public' },
        { title: t('navArchivedTournaments'), path: '/tournaments/archived' },
      ],
    },
    {
      title: t('navProfile'),
      icon: 'user',
      pages: [
        { title: t('navSettings'), path: '/account/edit' },
        { title: t('navMessages'), path: '/account/messages' },
      ],
    },
  ], [t]);

  const ADMIN_LINKS = useMemo(() => [
    {
      title: t('navAdmin'),
      icon: 'sliders',
      pages: [
        { title: t('navUsers'), path: '/admin/users' },
        { title: t('navLeagues'), path: '/admin/leagues' },
        { title: t('navBookmakers'), path: '/admin/bookmakers' },
        { title: t('navLeagueBookmakers'), path: '/admin/league-bookmakers' },
        { title: t('navMatches'), path: '/admin/matches' },
        { title: t('navBpSettings'), path: '/admin/bp-settings' },
        { title: t('navDebugApi'), path: '/admin/debug-api' },
      ],
    },
  ], [t]);

  const LANDING_LINKS = useMemo(() => [
    { title: t('navHowItWorks'), href: '#how', icon: 'gear-solid' },
    { title: t('navLeagues'), href: '#leagues', icon: 'ball' },
    { title: t('navScoringLabel'), href: '#scoring', icon: 'star-solid' },
    { title: t('navFaq'), href: '#faq', icon: 'book-regular' },
  ], [t]);

  // Build visible accordion links based on auth state (HOME_LINK is rendered separately as flat link)
  const visibleLinks = useMemo(() => [
    ...(isAuthenticated ? AUTH_LINKS : []),
    ...(isAdmin ? ADMIN_LINKS : []),
  ], [AUTH_LINKS, ADMIN_LINKS, isAuthenticated, isAdmin]);

  // manually handle accordion expansion
  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : undefined);
  };

  // collapse opened accordion on route change when the drawer is temporary
  useEffect(() => {
    width < 1280 && setExpanded(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <StyledDrawer
      variant={width < 1920 ? 'temporary' : 'permanent'}
      anchor="left"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disableSwipeToOpen={true}
      sx={{
        '& .MuiDrawer-paper': {
          width: 250,
        }
      }}
      className="main-sidebar"
    >
      <div className="logo-wrapper">
        <Logo size="sm" />
      </div>
      <nav className="d-flex flex-column g-8 flex-1">
        {isLandingPage ? (
          // Flat landing page links (no accordion)
          LANDING_LINKS.map((link) => (
            <SingleLink key={link.href} as="div">
              <a href={link.href} onClick={() => setOpen(false)}>
                <Link className="h4">
                  <i className={`icon icon-${link.icon}`} /> {link.title}
                </Link>
              </a>
            </SingleLink>
          ))
        ) : (
          <>
            {/* Home flat link */}
            <SingleLink as="div">
              <NavLink to={HOME_LINK.path} end data-testid="sidebar-nav-home">
                <Link className={`${pathname === HOME_LINK.path ? 'active' : ''} h4`}>
                  <i className={`icon icon-${HOME_LINK.icon}`} /> {HOME_LINK.title}
                </Link>
              </NavLink>
            </SingleLink>

            {/* Standard accordion navigation */}
            {visibleLinks.map((link, index) => (
              <StyledAccordion
                key={link.title}
                expanded={expanded === `panel${index}`}
                onChange={handleChange(`panel${index}`)}
              >
                <StyledAccordionSummary>
                  <Link className={`${expanded === `panel${index}` ? 'active' : ''} h4`}>
                    <i className={`icon icon-${link.icon}`} /> {link.title}
                  </Link>
                  <i className="icon icon-chevron-down" />
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  {link.pages.map(page => (
                    <NavLink to={page.path} key={page.title}>
                      {page.title}
                    </NavLink>
                  ))}
                </StyledAccordionDetails>
              </StyledAccordion>
            ))}
          </>
        )}
      </nav>

      {/* Auth Section at Bottom - for unauthenticated users */}
      {!isAuthenticated && (
        <>
          <SingleLink className={pathname === '/login' ? 'pinned active' : 'pinned'} as="div">
            <NavLink to="/login">
              <Link className={`${pathname === '/login' ? 'active' : ''} h4`}>
                <i className="icon icon-user" /> {t('navSignIn')}
              </Link>
            </NavLink>
          </SingleLink>
          <SingleLink className={pathname === '/sign-up' ? 'active' : ''} as="div">
            <NavLink to="/sign-up">
              <Link className={`${pathname === '/sign-up' ? 'active' : ''} h4`}>
                <i className="icon icon-plus" /> {t('navSignUp')}
              </Link>
            </NavLink>
          </SingleLink>
        </>
      )}

      {/* Language Switcher */}
      <div style={{ padding: '12px 20px' }}>
        <LanguageSwitcher />
      </div>
    </StyledDrawer>
  );
};

export default Sidebar;
export { Sidebar as SidebarNav };
